import { PhoneOutgoing } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { callBackDetail, callBackTitle, callingBackDone, standingCallBack } from "@/lib/call-back";
import { useAcdMe, useAgentAction, useCallBackMemory, useSetCallBackMemory } from "@/lib/queries";
import { useNow } from "@/lib/use-now";
import { useTakeCallsBlock } from "@/softphone/availability";
import { useOutboundDial } from "@/softphone/manual-call";

/**
 * The way back after a dropped call (platform ADR-085; `lib/call-back.ts`).
 *
 * "Call dropped" put the rep on the phone's own break, Calling back, and left the lead here. The
 * card offers the call: Call back now places it through the same campaign, as a hand dial from a
 * break is placed (ADR-068), and the manual-call card takes over; Not now ends the break and the
 * rep is Ready. The offer stands as long as the dialer's own wait does, and then goes away — the
 * dialer is calling the lead for the rep by then.
 *
 * Not now waits, as Ready does, for the phone to be connected and the microphone allowed
 * (`takeCallsBlock`, ACL-553): a line that dropped can take the phone with it.
 */
export function CallBackView() {
  const memory = useCallBackMemory();
  const forget = useSetCallBackMemory();
  const now = useNow(30_000);
  const cb = standingCallBack(memory, now);
  const out = useOutboundDial();
  const act = useAgentAction();
  const toast = useToast();
  const [placing, setPlacing] = React.useState(false);
  const blocked = useTakeCallsBlock();
  if (!cb) return null;

  const callBack = async () => {
    setPlacing(true);
    try {
      await out.place(cb.phone, cb.campaignId);
      forget(null);
    } catch (e) {
      toast.error(
        "Could not place the call back",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    } finally {
      setPlacing(false);
    }
  };
  const notNow = () => {
    forget(null);
    act.mutate([{ action: "unpause", queue: null }]);
  };

  return (
    <section
      aria-label="Call back"
      className="flex flex-col gap-2 rounded-lg border border-accent-8 bg-accent-2 p-3"
    >
      <p className="text-sm font-semibold text-text">{callBackTitle(cb)}</p>
      <p className="text-xs text-text-muted">{callBackDetail(cb)}</p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={() => void callBack()} disabled={placing}>
          <PhoneOutgoing className="size-4" aria-hidden />
          Call back now
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={notNow}
          disabled={placing || blocked !== null}
        >
          Not now
        </Button>
      </div>
      {blocked && <p className="text-xs text-text-muted">{blocked.text}</p>}
    </section>
  );
}

/**
 * Ends the phone's own break by itself once there is nobody left to call back: the call was placed
 * and the ACD put the rep back on the break when it ended (ADR-068), or the offer went stale. The
 * rep reads Ready again without a click, as they were before the line dropped — once the phone is
 * connected and the microphone allowed (`takeCallsBlock`, ACL-553). Until then they stay on the
 * break, and go Ready by themselves the moment both work.
 */
export function useCallingBackReturn(): void {
  const { user } = useAuth();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const memory = useCallBackMemory();
  const now = useNow(30_000);
  const act = useAgentAction();
  const { mutate } = act;
  const standing = standingCallBack(memory, now);
  const done = callingBackDone(me?.state ?? null, standing);
  const blocked = useTakeCallsBlock() !== null;
  React.useEffect(() => {
    if (!done || blocked) return;
    mutate([{ action: "unpause", queue: null }]);
  }, [done, blocked, mutate]);
}
